import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { CommonModule, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { IListing, IListingDetail, IMyRvProfile, getListingDetail, rvTypeLabel, ReviewService, CATEGORY_META, CANCELLATION_TIER_META, AMENITY_LABELS, buildListingSlugSegments } from '@cnt-workspace/data-access';

@Component({
  selector: 'cnt-listing-card',
  standalone: true,
  // Fill the grid cell, so the search wrapper's hover/selected ring traces the
  // card instead of the taller row.
  host: { class: 'block h-full' },
  imports: [CommonModule, RouterLink, NgOptimizedImage],
  templateUrl: './listing-card.component.html',
})
export class ListingCardComponent implements AfterViewInit, OnDestroy {
  private reviews = inject(ReviewService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private platformId = inject(PLATFORM_ID);
  @ViewChild('photoArea') private photoArea?: ElementRef<HTMLElement>;

  @Input({ required: true }) listing!: IListing;
  /** Set on the first card in a results grid so NgOptimizedImage's LCP
   *  detection has a priority-marked candidate instead of warning (NG02955)
   *  that the image it picked as Largest Contentful Paint wasn't prioritized. */
  @Input() priority = false;
  @Input() isFavorite = false;
  /** Optional. When > 0, the price chip reveals a "$price × N nights = $total" bubble on hover. */
  @Input() nights = 0;
  /** Optional. Tax for the selected dates; when set the total chip includes it. */
  @Input() taxTotal: number | null = null;
  /** Optional. When set, the card shows a "Fits / Too long for your <rig>" pill. */
  @Input() rvProfile: IMyRvProfile | null = null;
  /** When true (search results), the card ends with an action row: Reserve
   *  ("View site" for boondocking) plus an Add to trip icon button. */
  @Input() showAddToTrip = false;
  /** When true, a "Show on map" icon sits in the location row (below lg only,
   *  where the map is a separate view). Emits viewOnMap. */
  @Input() showMapButton = false;
  /** Optional "12 mi from Moab". Set by search results so a card under a
   *  "Stays near X" heading says how near it actually is (user-test F3).
   *  Empty when there is nothing to measure from. */
  @Input() distanceLabel = '';
  /** P39/B1 — count of bookings in the last 30 days. Renders a small
   *  social-proof pill on the card when >= 2.
   *  TODO: upstream `bookingsCountFor()` (search-results.component.ts) is a
   *  disabled mock and always passes undefined — this pill doesn't render
   *  in practice today. Needs a real ReservationEntity aggregation before
   *  re-enabling. */
  @Input() bookings30d?: number = 0;
  /** P39/B2 — surface a "verified host" badge on the card title row
   *  when true. Independent of the listing's category. */
  // @Input() hostVerified = false;
  @Output() favoriteToggle = new EventEmitter<MouseEvent>();
  @Output() addToTripClick = new EventEmitter<MouseEvent>();
  /** P39/A4 — click handler for the "View on map" mini-button. */
  @Output() viewOnMap = new EventEmitter<MouseEvent>();

  /** P39/A3 — transient flag that triggers the heart-burst animation
   *  for ~350 ms after a favorite toggle. */
  justFavorited = false;

  get hoverTotal(): number | null {
    if ('agency' in this.listing) return null;
    return this.nights > 0 ? this.listing.price * this.nights + (this.taxTotal ?? 0) : null;
  }

  /** Cancellation tier worth surfacing on the card — only the
   *  guest-favorable tiers (free-cancellation policies). Strict and
   *  exclusive don't earn a chip; they'd hurt the conversion signal
   *  instead of helping it. Null for boondocking.
   *
   *  Reads the listing's OWN tier, not `this.detail.cancellationTier` —
   *  `getListingDetail()` hands every card a generic 'moderate' placeholder, so
   *  reading it here made every card advertise "Free cancel · Moderate" (3 days)
   *  regardless of the real policy, including on listings that are actually
   *  Easy Goin' (1 day). This chip is a promise about refunds, so an unknown tier
   *  renders nothing at all rather than a guess. */
  get goodCancellationChip(): { label: string; summary: string } | null {
    if ('agency' in this.listing) return null;
    const tier = this.listing.cancellationTier;
    if (tier !== 'easy-goin' && tier !== 'moderate') return null;
    const meta = CANCELLATION_TIER_META[tier];
    return { label: meta.label, summary: meta.summary };
  }

  /** Numeric price for the template — narrowed so the union doesn't trip up Angular. */
  get listingPrice(): number {
    return 'agency' in this.listing ? 0 : this.listing.price;
  }

  /** SEO slug route to this card's detail page — `/rv-camping/...` or
   *  `/boondocking/...`, matching `buildListingSlugSegments`'s shape. */
  get slugRoute(): string[] {
    return buildListingSlugSegments('agency' in this.listing ? 'boondocking' : 'rv-camping', this.listing);
  }

  /** The rig, carried across the click into the detail page (F9).
   *
   *  `buildListingSlugSegments()` deals in path segments only and is shared by
   *  eight-plus call sites, so the rig rides alongside it as query params rather
   *  than being wedged into the path. Empty — and so absent from the URL — when
   *  no rig is in play, which is the common signed-out case. */
  @Input() rigParams: Record<string, string> = {};
  get slugQueryParams(): Record<string, string> {
    return this.rigParams;
  }

  currentImageIndex = 0;
  CATEGORY_META = CATEGORY_META;
  AMENITY_LABELS = AMENITY_LABELS;

  /** P45/B — labels for amenities beyond the chip limit,
   *  surfaced as a `title` tooltip on the "+N more" chip. */
  readonly AMENITY_CHIP_LIMIT = 2;

  get remainingAmenityLabels(): string {
    return this.listing.amenities.slice(this.AMENITY_CHIP_LIMIT).map(a => AMENITY_LABELS[a]).join(', ');
  }

  /** Full listing detail, memoized per listing — drives both the photo
   * carousel and the per-rig fit pill from a single getListingDetail call. */
  private _detail: IListingDetail | null = null;
  private _detailForId: number | null = null;
  private get detail(): IListingDetail {
    if (this._detailForId !== this.listing.id) {
      this._detail = getListingDetail(this.listing);
      this._detailForId = this.listing.id;
      this.currentImageIndex = 0;
    }
    return this._detail!;
  }

  /** Resolved photo array (up to 5) for the carousel. */
  get photos(): string[] {
    return this.detail.photos.slice(0, 5);
  }

  /** Fit verdict for the active RV profile against this listing's max rig
   * length. Null when no profile is supplied, it has no length, or this listing's
   * real cap isn't known. Mirrors the listing-detail `myRvFit` getter.
   *
   * Reads the listing's OWN `maxRigLength`, not `this.detail.siteSpecs.maxRigLength` —
   * that is a flat 35 placeholder from `getListingDetail()`, so every card returned the
   * same verdict no matter what the listing accepts. Real caps vary (40 ft listings were
   * being shown as "Too long" to rigs they would have taken). An unknown cap renders no
   * pill: "will my rig fit" is the question the whole product is answering, and a
   * confident wrong answer sends someone down a road they can't turn around on. */
  get rvFit(): { passes: boolean; label: string } | null {
    const rv = this.rvProfile;
    if (!rv || !rv.length) return null;
    const max = 'agency' in this.listing ? undefined : this.listing.maxRigLength;
    if (typeof max !== 'number' || max <= 0) return null;
    const name = rv.name?.trim() || rvTypeLabel(rv.type);
    return rv.length > max
      ? { passes: false, label: `Too long for your ${name}` }
      : { passes: true,  label: `Fits your ${name}` };
  }

  /**
   * Merchandising badge — at most one per card. Guest Favorite is the only one with a
   * real backend signal today (rating ≥ 4.8); "New"/"Best Value" were retired along
   * with the mock seed data they were computed from — there's no real `createdAt`
   * field to derive "New" from, and no defined real signal for "Best Value" yet.
   * Returns null when none apply.
   */
  get merchBadge(): { label: string; icon: string; bg: string; fg: string } | null {
    if (!('agency' in this.listing) && this.listing.rating >= 4.8) {
      return { label: 'Guest Favorite', icon: 'workspace_premium', bg: 'bg-dark-text', fg: 'text-gold' };
    }
    return null;
  }

  /** Aggregated rating + count combining the seeded baseline with any
   * user-submitted reviews. Boondocking listings have no rating; callers
   * should already guard on kind. */
  get aggregatedRating(): { rating: number; count: number } {
    if ('agency' in this.listing) return { rating: 0, count: 0 };
    return this.reviews.aggregateRating(this.listing.rating, this.listing.reviewCount, this.listing.id);
  }

  /** Add-on count for the "+N add-ons" badge. Real count from the backend
   * (`IPrivateListing.extrasCount`); boondocking listings never have add-ons. */
  get addOnCount(): number {
    return 'agency' in this.listing ? 0 : (this.listing.extrasCount ?? 0);
  }

  onFavoriteClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // P39/A3 — burst animation for the heart when toggling on. Skip
    // the animation when un-favoriting so the disappearing pulse
    // doesn't read as a celebration.
    if (!this.isFavorite) {
      this.justFavorited = true;
      setTimeout(() => { this.justFavorited = false; }, 350);
    }
    this.favoriteToggle.emit(event);
  }

  onViewOnMapClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.viewOnMap.emit(event);
  }

  onAddToTripClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.addToTripClick.emit(event);
  }

  prevImage(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const n = this.photos.length;
    this.currentImageIndex = (this.currentImageIndex - 1 + n) % n;
  }

  /** Touch/pen swipe on the photo: start point, and whether the last gesture was a swipe. */
  photoPointerStart: { x: number; y: number } | null = null;
  private swipedPhoto = false;
  private readonly SWIPE_MIN_PX = 40;
  private readonly listeners: [string, EventListener, boolean][] = [
    ['pointerdown', (e) => this.onPhotoPointerDown(e as PointerEvent), false],
    ['pointerup', (e) => this.onPhotoPointerUp(e as PointerEvent), false],
    ['pointercancel', () => { this.photoPointerStart = null; }, false],
    // Capture phase, so a swipe's trailing click is stopped before the stretched
    // title link (the actual click target) can navigate.
    ['click', (e) => this.onPhotoClickCapture(e as MouseEvent), true],
  ];

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    for (const [type, fn, capture] of this.listeners) this.host.nativeElement.addEventListener(type, fn, capture);
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    for (const [type, fn, capture] of this.listeners) this.host.nativeElement.removeEventListener(type, fn, capture);
  }

  onPhotoPointerDown(event: PointerEvent): void {
    this.swipedPhoto = false;
    this.photoPointerStart = null;
    if (event.pointerType === 'mouse' || this.photos.length < 2) return;
    const area = this.photoArea?.nativeElement.getBoundingClientRect();
    if (!area || event.clientX < area.left || event.clientX > area.right || event.clientY < area.top || event.clientY > area.bottom) return;
    this.photoPointerStart = { x: event.clientX, y: event.clientY };
  }

  onPhotoPointerUp(event: PointerEvent): void {
    const start = this.photoPointerStart;
    this.photoPointerStart = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < this.SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
    const n = this.photos.length;
    this.currentImageIndex = dx < 0 ? (this.currentImageIndex + 1) % n : (this.currentImageIndex - 1 + n) % n;
    this.swipedPhoto = true;
  }

  /** A swipe is followed by a click on some browsers; don't let it open the listing. */
  onPhotoClickCapture(event: MouseEvent): void {
    if (!this.swipedPhoto) return;
    this.swipedPhoto = false;
    event.preventDefault();
    event.stopPropagation();
  }

  nextImage(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const n = this.photos.length;
    this.currentImageIndex = (this.currentImageIndex + 1) % n;
  }

  goToImage(i: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentImageIndex = i;
  }
}
